import { Button, FormControl, FormHelperText, HStack, Input, useToast } from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { HTTP } from 'utils/http';
import type { CheckoutData } from '../useCheckoutForm';

/**
 * Logique d'application d'une carte cadeau au panier (partagée entre
 * l'ancien récapitulatif et le Nouveau Panier v2). Ne fait qu'écrire
 * `GiftCard` / `GiftCardBalance` dans le formulaire ; la vérification
 * définitive du solde est refaite côté serveur au paiement.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useGiftCardApply() {
  const toast = useToast();
  const { setValue, watch } = useFormContext<CheckoutData>();
  const [isLoading, setIsLoading] = useState(false);
  const [giftCardAmount, amount, giftCardCode] = watch(['GiftCardBalance', 'Amount', 'GiftCard']);

  async function apply(rawCode: string | undefined): Promise<boolean> {
    const id = rawCode?.trim();
    if (!id) return false;

    setIsLoading(true);
    setValue('GiftCard', '');
    setValue('GiftCardBalance', undefined);
    const giftCardAmount = await HTTP.get<number>(`/sale/giftcard/${id}`)
      .send()
      .finally(() => setIsLoading(false));

    if (giftCardAmount === 0) {
      toast({
        title: `Le code "${id}" est invalide`,
        status: 'error',
      });
      return false;
    }

    setValue('GiftCard', id);
    setValue('GiftCardBalance', giftCardAmount);
    return true;
  }

  function clear() {
    setValue('GiftCard', '');
    setValue('GiftCardBalance', undefined);
  }

  const remainingOnCard = giftCardAmount ? Math.max(giftCardAmount - amount, 0) : 0;

  return { apply, clear, isLoading, giftCardAmount, giftCardCode, amount, remainingOnCard };
}

export function CheckoutGiftCard() {
  const { apply, isLoading, giftCardAmount, amount } = useGiftCardApply();
  const giftCardInputRef = useRef<HTMLInputElement | null>(null);

  async function checkGiftCardBalance() {
    const ok = await apply(giftCardInputRef?.current?.value);
    if (!ok) giftCardInputRef.current?.focus();
  }

  return (
    <FormControl>
      <HStack className="GiftCardInput">
        <Input
          ref={giftCardInputRef}
          placeholder="Utiliser une carte cadeau"
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              ev.preventDefault();
              checkGiftCardBalance();
            }
          }}
        />
        <Button onClick={checkGiftCardBalance} isLoading={isLoading}>
          Appliquer
        </Button>
      </HStack>

      {giftCardAmount && giftCardAmount > 0 && (
        <FormHelperText>
          Cette carte cadeau à une valeur de {giftCardAmount} €, il restera{' '}
          {giftCardAmount - amount < 0 ? 0 : giftCardAmount - amount} € sur la carte cadeau après le paiement.
        </FormHelperText>
      )}
    </FormControl>
  );
}
